import React, { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useColors } from "../constants/Colors";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useSidebar } from "../contexts/SidebarContext";
import { useUpload } from "../contexts/UploadContext";
import { useProfile } from "../contexts/ProfileContext";
import { queryClient, realtimeClient, trpcReact } from "../trpc_client";
import {
  Search,
  MessageSquare,
  Inbox,
  ClipboardList,
  User,
  Menu,
  LogOut,
  Mail,
  Instagram,
  ChevronsLeft,
  ChevronsRight,
  Globe,
  Info,
  Upload,
  CheckCircle,
  XCircle,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const tabs = [
  {
    to: "/",
    labelKey: "search_tab.tabbar_title",
    fallback: "Search",
    Icon: Search,
  },
  {
    to: "/chats",
    labelKey: "chats_tab.tabbar_title",
    fallback: "Chats",
    Icon: MessageSquare,
  },
  {
    to: "/applications",
    labelKey: "applications_tab.tabbar_title",
    fallback: "Applications",
    Icon: Inbox,
  },
  {
    to: "/flats",
    labelKey: "flats_tab.tabbar_title",
    fallback: "Flats",
    Icon: ClipboardList,
  },
  {
    to: "/profile",
    labelKey: "profile_tab.tabbar_title",
    fallback: "Profile",
    Icon: User,
    isProfile: true,
  },
];

type SidebarMessageState = {
  user_id: number;
  state: "delivered" | "read";
};

type SidebarMessage = {
  sender_id: number;
  message_state?: SidebarMessageState[];
};

type SidebarChat = {
  conversation_id: number;
  state: string;
  messages: SidebarMessage[];
};

const SidebarTabs: React.FC = () => {
  const colors = useColors();
  const { t, i18n } = useTranslation();
  const { user, signOut } = useAuth();
  const { isCollapsed, toggleCollapsed } = useSidebar();
  const {
    upload,
    clearUpload,
    isDetailModalOpen,
    openDetailModal,
    closeDetailModal,
    showToast,
    toastMessage,
  } = useUpload();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const uploadModalRef = useRef<HTMLDivElement>(null);
  const realtimeSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(
    null,
  );
  const deliveredSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(
    null,
  );
  const readSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const reactionSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(
    null,
  );
  const location = useLocation();
  const navigate = useNavigate();

  const currentLanguage = i18n.language?.startsWith("de") ? "de" : "en";

  const { profile, isProfileComplete, userExistsInDb } = useProfile();

  // Only show profile avatar if profile is complete (has image)
  const profileAvatar =
    isProfileComplete && profile?.profile_images?.[0]
      ? profile.profile_images[0]
      : undefined;

  // Only show profile name if user exists in database, otherwise show default "Profile" text
  const profileName = userExistsInDb
    ? profile?.username || user?.displayName
    : undefined;

  const { data: currentUser } = trpcReact.getCurrentUserProfile.useQuery(
    undefined,
    {
      enabled: Boolean(user && userExistsInDb),
    },
  );

  const { data: chats } = trpcReact.getChats.useQuery(
    { user_id: currentUser?.user_id ?? 0 },
    {
      enabled: Boolean(user && currentUser?.user_id),
      refetchOnWindowFocus: true,
    },
  );

  const hasUnreadMessages = (chat: SidebarChat): boolean => {
    if (!currentUser?.user_id) return false;
    if (chat.state === "archived") return false;
    return chat.messages.some(
      (message) =>
        message.sender_id !== currentUser.user_id &&
        !message.message_state?.some(
          (state) =>
            state.user_id === currentUser.user_id && state.state === "read",
        ),
    );
  };

  const unreadChatsCount =
    (chats as SidebarChat[] | undefined)?.filter((chat) =>
      hasUnreadMessages(chat),
    ).length ?? 0;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (
        uploadModalRef.current &&
        !uploadModalRef.current.contains(event.target as Node)
      ) {
        closeDetailModal();
      }
    };

    if (isMenuOpen || isDetailModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen, isDetailModalOpen, closeDetailModal]);

  useEffect(() => {
    if (!user) {
      realtimeClient.setIsAuthenticated(false);
      if (realtimeSubscriptionRef.current) {
        realtimeSubscriptionRef.current.unsubscribe();
        realtimeSubscriptionRef.current = null;
      }
      if (deliveredSubscriptionRef.current) {
        deliveredSubscriptionRef.current.unsubscribe();
        deliveredSubscriptionRef.current = null;
      }
      if (readSubscriptionRef.current) {
        readSubscriptionRef.current.unsubscribe();
        readSubscriptionRef.current = null;
      }
      if (reactionSubscriptionRef.current) {
        reactionSubscriptionRef.current.unsubscribe();
        reactionSubscriptionRef.current = null;
      }
      return;
    }

    realtimeClient.setIsAuthenticated(true);
  }, [user]);

  useEffect(() => {
    if (!user || !currentUser?.user_id) return;

    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "default"
    ) {
      void Notification.requestPermission();
    }

    if (realtimeSubscriptionRef.current) {
      realtimeSubscriptionRef.current.unsubscribe();
      realtimeSubscriptionRef.current = null;
    }
    if (deliveredSubscriptionRef.current) {
      deliveredSubscriptionRef.current.unsubscribe();
      deliveredSubscriptionRef.current = null;
    }
    if (readSubscriptionRef.current) {
      readSubscriptionRef.current.unsubscribe();
      readSubscriptionRef.current = null;
    }
    if (reactionSubscriptionRef.current) {
      reactionSubscriptionRef.current.unsubscribe();
      reactionSubscriptionRef.current = null;
    }

    const client = realtimeClient.getClient();
    if (!client) return;

    const subscription = client.chat.conversation.subscribe(undefined, {
      onData: (message: unknown) => {
        const incomingMessage = message as {
          sender_id?: number;
          message?: string;
        };
        queryClient.invalidateQueries({ queryKey: [["getChats"]] });
        if (
          typeof window !== "undefined" &&
          document.hidden &&
          incomingMessage.sender_id &&
          incomingMessage.sender_id !== currentUser.user_id &&
          "Notification" in window &&
          Notification.permission === "granted"
        ) {
          const body =
            incomingMessage.message && incomingMessage.message.trim().length > 0
              ? incomingMessage.message
              : "You received a new message on Flateroo.";
          new Notification("New message", { body });
        }
      },
    });

    realtimeSubscriptionRef.current = subscription;
    const deliveredSubscription = client.chat.messageDelivered.subscribe(
      undefined,
      {
        onData: () => {
          queryClient.invalidateQueries({ queryKey: [["getChats"]] });
        },
      },
    );
    deliveredSubscriptionRef.current = deliveredSubscription;

    const readSubscription = client.chat.messageRead.subscribe(undefined, {
      onData: () => {
        queryClient.invalidateQueries({ queryKey: [["getChats"]] });
      },
    });
    readSubscriptionRef.current = readSubscription;

    const reactionSubscription = client.chat.messageReaction.subscribe(
      undefined,
      {
        onData: () => {
          queryClient.invalidateQueries({ queryKey: [["getChats"]] });
        },
      },
    );
    reactionSubscriptionRef.current = reactionSubscription;

    return () => {
      subscription.unsubscribe();
      deliveredSubscription.unsubscribe();
      readSubscription.unsubscribe();
      reactionSubscription.unsubscribe();
      realtimeSubscriptionRef.current = null;
      deliveredSubscriptionRef.current = null;
      readSubscriptionRef.current = null;
      reactionSubscriptionRef.current = null;
    };
  }, [user, currentUser?.user_id]);

  const handleLogout = async () => {
    setIsMenuOpen(false);
    await signOut();
  };

  const handleContact = () => {
    setIsMenuOpen(false);
    window.location.href = "mailto:support@flateroo.com";
  };

  const handleInstagram = () => {
    setIsMenuOpen(false);
    window.open("https://www.instagram.com/flateroo_app/", "_blank");
  };

  const handleLanguageToggle = () => {
    const newLang = currentLanguage === "en" ? "de" : "en";
    i18n.changeLanguage(newLang);
    Cookies.set("language", newLang, { sameSite: "None", secure: true });
    setIsMenuOpen(false);
  };

  const handleAbout = () => {
    setIsMenuOpen(false);
    navigate("/about");
  };

  // Hide mobile tab bar on full-screen pages
  const hideBottomTab =
    /^\/chats\/\d+/.test(location.pathname) ||
    location.pathname === "/create-flat" ||
    location.pathname === "/signup";

  return (
    <>
      {/* Mobile Bottom Tabbar */}
      <nav
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-30 px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] ${hideBottomTab ? "hidden" : ""}`}
      >
        <div
          className="rounded-[22px] py-1.5 px-0 flex items-center justify-around"
          style={{
            backgroundColor: colors.containerNeutralSolidSurface,
            boxShadow:
              "0 2px 12px rgba(2, 2, 16, 0.08), inset 0 0 0 1px rgba(2, 2, 16, 0.04)",
          }}
        >
          {tabs.map(({ to, labelKey, fallback, Icon, isProfile }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className="active:scale-[0.85] transition-transform duration-150"
              aria-label={t(labelKey) || fallback}
            >
              {({ isActive }) => (
                <div
                  className={`flex items-center gap-2 rounded-[16px] transition-all duration-300 ${
                    isActive ? "py-2.5 px-4" : "py-2.5 px-3"
                  }`}
                  style={{
                    backgroundColor: isActive
                      ? `${colors.accentColor}14`
                      : undefined,
                    color: isActive
                      ? colors.accentColor
                      : colors.textNeutralMuted,
                    transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
                  }}
                >
                  <div className="relative flex-shrink-0">
                    {isProfile && profileAvatar ? (
                      <img
                        src={profileAvatar}
                        alt={profileName || "Profile"}
                        className="h-[22px] w-[22px] rounded-full object-cover transition-shadow duration-300"
                        style={{
                          boxShadow: isActive
                            ? `0 0 0 2px ${colors.accentColor}`
                            : "0 0 0 2px transparent",
                        }}
                      />
                    ) : (
                      <Icon
                        size={22}
                        strokeWidth={isActive ? 2.2 : 1.5}
                        className="transition-all duration-200"
                      />
                    )}
                    {to === "/chats" && unreadChatsCount > 0 && (
                      <span
                        className="absolute -top-1.5 -right-1.5 min-w-[1rem] h-4 px-1 rounded-full text-[10px] font-semibold flex items-center justify-center"
                        style={{
                          backgroundColor: colors.accentColor,
                          color: colors.textNeutralOnPrimary,
                          boxShadow: `0 2px 8px ${colors.accentColor}50`,
                        }}
                      >
                        {unreadChatsCount > 99 ? "99+" : unreadChatsCount}
                      </span>
                    )}
                  </div>
                  {isActive && (
                    <span className="text-[13px] font-semibold leading-none whitespace-nowrap">
                      {isProfile && profileName
                        ? profileName
                        : t(labelKey) || fallback}
                    </span>
                  )}
                </div>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Desktop Sidebar */}
      <nav
        className={`hidden lg:flex fixed inset-y-0 left-0 z-30 flex-col py-8 px-2 transition-all duration-300 ease-in-out ${
          isCollapsed ? "w-16" : "w-56"
        } `}
        style={{
          backgroundColor: colors.containerNeutralSolidSurface,
        }}
      >
        {/* Logo at top */}
        <div className="flex items-center px-3  pb-8 overflow-hidden ">
          <img
            src={"/f_only.png"}
            alt="Flateroo"
            width={18}
            height={32}
            className="h-8 transition-all duration-300"
          />
        </div>

        {/* Navigation tabs - centered vertically */}
        <div className="flex-1 flex flex-col justify-center">
          <div className="flex flex-col gap-2">
            {tabs.map(({ to, labelKey, fallback, Icon, isProfile }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `flex items-center gap-4 px-3 py-3 rounded-2xl transition-all hover:bg-black/5 overflow-hidden ${
                    isActive ? "font-semibold" : "font-medium"
                  }`
                }
                style={({ isActive }) => ({
                  backgroundColor: isActive
                    ? colors.containerNeutralLightLowest
                    : undefined,
                  color: colors.textNeutralDefault,
                })}
                end={to === "/"}
                title={isCollapsed ? t(labelKey) || fallback : undefined}
              >
                <div className="flex-shrink-0 relative">
                  {isProfile && profileAvatar ? (
                    <img
                      src={profileAvatar}
                      alt={profileName || "Profile"}
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  ) : (
                    <Icon size={24} strokeWidth={1.5} />
                  )}
                  {to === "/chats" && unreadChatsCount > 0 && (
                    <span
                      className="absolute -top-1.5 -right-1.5 min-w-[1.1rem] h-4 px-1 rounded-full text-[10px] font-semibold flex items-center justify-center"
                      style={{
                        backgroundColor: colors.accentColor,
                        color: colors.textNeutralOnPrimary,
                      }}
                    >
                      {unreadChatsCount > 99 ? "99+" : unreadChatsCount}
                    </span>
                  )}
                </div>
                <span
                  className={`text-base whitespace-nowrap transition-all duration-300 ${
                    isCollapsed ? "opacity-0 w-0" : "opacity-100 w-auto"
                  }`}
                >
                  {profileName && isProfile
                    ? profileName
                    : t(labelKey) || fallback}
                </span>
              </NavLink>
            ))}
          </div>
        </div>

        {/* Upload Progress Indicator - Clickable */}
        {(upload.isUploading || upload.progress === 100 || upload.error) && (
          <div className="relative mb-4" ref={uploadModalRef}>
            <button
              onClick={openDetailModal}
              className={`w-full rounded-2xl p-4 transition-all duration-300 hover:scale-[1.02] cursor-pointer ${
                isCollapsed ? "mx-1" : ""
              }`}
              style={{
                backgroundColor: upload.error
                  ? "rgba(239, 68, 68, 0.1)"
                  : upload.progress === 100
                    ? "rgba(34, 197, 94, 0.1)"
                    : colors.containerNeutralLightLowest,
              }}
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {upload.error ? (
                    <XCircle
                      size={isCollapsed ? 24 : 28}
                      className="text-red-500"
                    />
                  ) : upload.progress === 100 ? (
                    <CheckCircle
                      size={isCollapsed ? 24 : 28}
                      className="text-green-500"
                    />
                  ) : (
                    <Upload
                      size={isCollapsed ? 24 : 28}
                      style={{ color: colors.accentColor }}
                      className="animate-pulse"
                    />
                  )}
                </div>
                {!isCollapsed && (
                  <div className="flex-1 min-w-0 text-left">
                    <p
                      className="text-sm font-medium truncate"
                      style={{ color: colors.textNeutralDefault }}
                    >
                      {upload.error
                        ? t("upload_failed") || "Upload failed"
                        : upload.progress === 100
                          ? t("upload_complete") || "Upload complete!"
                          : t("uploading_video") || "Uploading video..."}
                    </p>
                    <p
                      className="text-xs truncate mt-0.5"
                      style={{ color: colors.textNeutralMuted }}
                    >
                      {t("click_for_details") || "Click for details"}
                    </p>
                    {upload.isUploading && (
                      <div
                        className="w-full h-1.5 rounded-full mt-2 overflow-hidden"
                        style={{
                          backgroundColor: colors.containerNeutralLightMedium,
                        }}
                      >
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{
                            backgroundColor: colors.accentColor,
                            width: `${upload.progress}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              {isCollapsed && (
                <div
                  className="w-full h-1 rounded-full mt-2 overflow-hidden"
                  style={{
                    backgroundColor: colors.containerNeutralLightMedium,
                  }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: upload.error
                        ? "#ef4444"
                        : upload.progress === 100
                          ? "#22c55e"
                          : colors.accentColor,
                      width: `${upload.progress}%`,
                    }}
                  />
                </div>
              )}
            </button>

            {/* Upload Detail Modal */}
            {isDetailModalOpen && (
              <div
                className={`absolute z-50 rounded-2xl shadow-2xl overflow-hidden border ${
                  isCollapsed
                    ? "left-full bottom-0 ml-2 w-80"
                    : "bottom-full left-0 right-0 mb-2"
                }`}
                style={{
                  backgroundColor: colors.containerNeutralSolidSurface,
                  borderColor: colors.containerNeutralLightMedium,
                }}
              >
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {upload.error ? (
                        <XCircle size={24} className="text-red-500" />
                      ) : upload.progress === 100 ? (
                        <CheckCircle size={24} className="text-green-500" />
                      ) : (
                        <Upload
                          size={24}
                          style={{ color: colors.accentColor }}
                          className="animate-pulse"
                        />
                      )}
                      <h3
                        className="text-lg font-semibold"
                        style={{ color: colors.textNeutralDefault }}
                      >
                        {t("video_application") || "Video Application"}
                      </h3>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        closeDetailModal();
                      }}
                      className="p-1 rounded-full hover:bg-black/10 transition-colors"
                    >
                      <X size={18} style={{ color: colors.textNeutralMuted }} />
                    </button>
                  </div>

                  {/* Flat name */}
                  {upload.flatName && (
                    <p
                      className="text-sm mb-3"
                      style={{ color: colors.textNeutralMuted }}
                    >
                      {t("applying_to") || "Applying to"}:{" "}
                      <span
                        className="font-medium"
                        style={{ color: colors.textNeutralDefault }}
                      >
                        {upload.flatName}
                      </span>
                    </p>
                  )}

                  {/* Progress bar */}
                  <div
                    className="w-full h-3 rounded-full overflow-hidden mb-2"
                    style={{
                      backgroundColor: colors.containerNeutralLightMedium,
                    }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        backgroundColor: upload.error
                          ? "#ef4444"
                          : upload.progress === 100
                            ? "#22c55e"
                            : colors.accentColor,
                        width: `${upload.progress}%`,
                      }}
                    />
                  </div>

                  {/* Progress percentage and status */}
                  <div className="flex items-center justify-between mb-4">
                    <p
                      className="text-sm font-medium"
                      style={{ color: colors.textNeutralDefault }}
                    >
                      {upload.progress}%
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: colors.textNeutralMuted }}
                    >
                      {upload.error || upload.status}
                    </p>
                  </div>

                  {/* Status message */}
                  {upload.isUploading && (
                    <div
                      className="p-3 rounded-xl flex items-start gap-2"
                      style={{ backgroundColor: `${colors.accentColor}15` }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={colors.accentColor}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="flex-shrink-0 mt-0.5"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                      <p
                        className="text-sm"
                        style={{ color: colors.textNeutralDefault }}
                      >
                        {t("upload_in_progress_message") ||
                          "Your video is being uploaded. You can continue browsing, but please don't close this tab until the upload is complete."}
                      </p>
                    </div>
                  )}

                  {upload.progress === 100 && !upload.error && (
                    <div
                      className="p-3 rounded-xl flex items-start gap-2"
                      style={{ backgroundColor: "rgba(34, 197, 94, 0.1)" }}
                    >
                      <CheckCircle
                        size={18}
                        className="text-green-500 flex-shrink-0 mt-0.5"
                      />
                      <p
                        className="text-sm"
                        style={{ color: colors.textNeutralDefault }}
                      >
                        {t("upload_success_message") ||
                          "Your video application has been sent successfully! The flat owners will be notified and can view your application."}
                      </p>
                    </div>
                  )}

                  {upload.error && (
                    <div
                      className="p-3 rounded-xl flex items-start gap-2"
                      style={{ backgroundColor: "rgba(239, 68, 68, 0.1)" }}
                    >
                      <XCircle
                        size={18}
                        className="text-red-500 flex-shrink-0 mt-0.5"
                      />
                      <p
                        className="text-sm"
                        style={{ color: colors.textNeutralDefault }}
                      >
                        {t("upload_error_message") ||
                          "Something went wrong with your upload. Please try again."}
                      </p>
                    </div>
                  )}

                  {/* Dismiss button for completed/failed uploads */}
                  {(upload.progress === 100 || upload.error) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearUpload();
                        closeDetailModal();
                      }}
                      className="w-full mt-4 py-2 rounded-full text-sm font-medium transition-colors hover:opacity-80"
                      style={{
                        backgroundColor: colors.containerNeutralLightMedium,
                        color: colors.textNeutralDefault,
                      }}
                    >
                      {t("dismiss") || "Dismiss"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Toast Notification */}
        {showToast && (
          <div
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-slide-up"
            style={{
              backgroundColor: colors.containerNeutralSolidSurface,
              border: `1px solid ${colors.containerNeutralLightMedium}`,
            }}
          >
            <Upload
              size={24}
              style={{ color: colors.accentColor }}
              className="animate-pulse"
            />
            <p
              className="text-sm font-medium"
              style={{ color: colors.textNeutralDefault }}
            >
              {toastMessage}
            </p>
          </div>
        )}

        {/* More menu at bottom */}
        <div className="relative" ref={menuRef}>
          {/* Popup menu */}
          {isMenuOpen && (
            <div
              className={`absolute z-50 overflow-hidden ${
                isCollapsed
                  ? "left-full bottom-0 ml-3 w-60"
                  : "bottom-full left-0 right-0 mb-3"
              }`}
              style={{
                backgroundColor: colors.containerNeutralSolidSurface,
                borderRadius: 20,
                boxShadow:
                  "0 8px 40px rgba(2, 2, 16, 0.12), 0 0 0 1px rgba(2, 2, 16, 0.06)",
                animation:
                  "scaleIn 0.18s cubic-bezier(0.16, 1, 0.3, 1) forwards",
                transformOrigin: "bottom left",
              }}
            >
              {/* Menu items */}
              <div className="p-2">
                {/* Language toggle — prominent with accent tint */}
                <button
                  onClick={handleLanguageToggle}
                  className="flex items-center gap-3.5 w-full px-3.5 py-3 rounded-xl transition-all duration-150"
                  style={{ color: colors.textNeutralDefault }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(91, 61, 246, 0.06)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "rgba(91, 61, 246, 0.08)" }}
                  >
                    <Globe
                      size={17}
                      strokeWidth={1.8}
                      style={{ color: colors.accentColor }}
                    />
                  </div>
                  <div className="flex-1 text-left">
                    <span className="text-sm font-medium">
                      {currentLanguage === "en" ? "Deutsch" : "English"}
                    </span>
                  </div>
                  <span
                    className="text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: "rgba(91, 61, 246, 0.08)",
                      color: colors.accentColor,
                    }}
                  >
                    {currentLanguage.toUpperCase()}
                  </span>
                </button>

                {/* Divider */}
                <div
                  className="mx-3 my-1.5"
                  style={{ height: 1, backgroundColor: "rgba(2, 2, 16, 0.06)" }}
                />

                {/* Contact */}
                <button
                  onClick={handleContact}
                  className="flex items-center gap-3.5 w-full px-3.5 py-2.5 rounded-xl transition-all duration-150"
                  style={{ color: colors.textNeutralDefault }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(2, 2, 16, 0.04)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <Mail
                    size={18}
                    strokeWidth={1.5}
                    style={{ color: colors.textNeutralMuted }}
                  />
                  <span className="text-sm">{t("contact") || "Contact"}</span>
                </button>

                {/* Instagram */}
                <button
                  onClick={handleInstagram}
                  className="flex items-center gap-3.5 w-full px-3.5 py-2.5 rounded-xl transition-all duration-150"
                  style={{ color: colors.textNeutralDefault }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(2, 2, 16, 0.04)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <Instagram
                    size={18}
                    strokeWidth={1.5}
                    style={{ color: colors.textNeutralMuted }}
                  />
                  <span className="text-sm">
                    {t("follow_us") || "Follow us"}
                  </span>
                </button>

                {/* About */}
                <button
                  onClick={handleAbout}
                  className="flex items-center gap-3.5 w-full px-3.5 py-2.5 rounded-xl transition-all duration-150"
                  style={{ color: colors.textNeutralDefault }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(2, 2, 16, 0.04)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <Info
                    size={18}
                    strokeWidth={1.5}
                    style={{ color: colors.textNeutralMuted }}
                  />
                  <span className="text-sm">{t("about") || "About"}</span>
                </button>

                {/* Logout */}
                {user && (
                  <>
                    <div
                      className="mx-3 my-1.5"
                      style={{
                        height: 1,
                        backgroundColor: "rgba(2, 2, 16, 0.06)",
                      }}
                    />
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3.5 w-full px-3.5 py-2.5 rounded-xl transition-all duration-150"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          "rgba(220, 54, 46, 0.06)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <LogOut
                        size={18}
                        strokeWidth={1.5}
                        style={{ color: "#DC362E" }}
                      />
                      <span
                        className="text-sm font-medium"
                        style={{ color: "#DC362E" }}
                      >
                        {t("logout") || "Log out"}
                      </span>
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* More button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`flex items-center gap-4 w-full px-3 py-3 rounded-2xl transition-all duration-200 overflow-hidden ${
              isMenuOpen ? "font-semibold" : "font-medium"
            }`}
            style={{
              backgroundColor: isMenuOpen
                ? "rgba(91, 61, 246, 0.06)"
                : undefined,
              color: isMenuOpen
                ? colors.accentColor
                : colors.textNeutralDefault,
            }}
            onMouseEnter={(e) => {
              if (!isMenuOpen)
                e.currentTarget.style.backgroundColor = "rgba(2, 2, 16, 0.04)";
            }}
            onMouseLeave={(e) => {
              if (!isMenuOpen)
                e.currentTarget.style.backgroundColor = "transparent";
            }}
            title={isCollapsed ? t("more") || "More" : undefined}
          >
            <div className="flex-shrink-0">
              <Menu size={24} strokeWidth={1.5} />
            </div>
            <span
              className={`text-base whitespace-nowrap transition-all duration-300 ${
                isCollapsed ? "opacity-0 w-0" : "opacity-100 w-auto"
              }`}
            >
              {t("more") || "More"}
            </span>
          </button>
        </div>

        {/* Collapse/Expand toggle button */}
        <button
          onClick={toggleCollapsed}
          className="mt-4 flex items-center gap-4 w-full px-3 py-3 rounded-2xl transition-all hover:bg-black/5 overflow-hidden"
          style={{ color: colors.textNeutralDefault }}
          title={
            isCollapsed
              ? t("expand_sidebar") || "Expand"
              : t("collapse_sidebar") || "Collapse"
          }
        >
          <div className="flex-shrink-0 transition-transform duration-300">
            {isCollapsed ? (
              <ChevronsRight size={24} strokeWidth={1.5} />
            ) : (
              <ChevronsLeft size={24} strokeWidth={1.5} />
            )}
          </div>
          <span
            className={`text-base whitespace-nowrap transition-all duration-300 ${
              isCollapsed ? "opacity-0 w-0" : "opacity-100 w-auto"
            }`}
          >
            {t("collapse_sidebar") || "Collapse"}
          </span>
        </button>
      </nav>
    </>
  );
};

export default SidebarTabs;
